import React, { useCallback, useState } from 'react';
import { Formik, Form } from 'formik';
import { FormikControl, LocationsDropdownWrapper, FormFailed, SelfScheduleSubmitting, MarketingConsentMsg } from '..';
import {
    NETLIFY_FUNCTIONS_PATH,
    CTA_CLICK_SMS_OPT_IN,
    callVerifyHuman,
    getFacebookCookies,
    getOptimizelyData,
    appendFormUrlToExposures,
    syncPardotTracking,
    isLeadsCaptureResponse,
    validateWithPossibleNow,
    appendGoogleLinkerParam,
    safeFullStory as FullStory,
} from '../../utils';
import { ConsultSuccess } from './CouponSuccess';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import axios from 'axios';
import { couponSchema } from '../../utils/formSchemas';
import { couponInitialValues } from './utils/couponInitialValues';
import { useSelfScheduleTimer, SELF_SCHEDULE_EVENTS } from '../../hooks/useSelfScheduleTimer';

export function BookConsult({ siteData, coupon }) {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const isBrowser = typeof window !== 'undefined';
    const { startTimer, pushTimingEvent, resetTimer } = useSelfScheduleTimer();
    // WEBDEV-3553: See ConsultationForm for full explanation. Same pattern applies here —
    // isRedirecting keeps SelfScheduleSubmitting visible after Formik resets isSubmitting.
    const [isRedirecting, setIsRedirecting] = useState(false);

    // Create an event handler so you can call the verification on button click event or form submit
    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            return;
        }
        const user = await callVerifyHuman('couponConsult', executeRecaptcha);
        return user;
        // Do whatever you want with the token
    }, [executeRecaptcha]);

    // Handle form submission
    const handleSubmit = async (values, helpers) => {
        const { fbp, fbc } = getFacebookCookies();
        const { experiment_exposures, experiment_user_ID } = getOptimizelyData();
        helpers.setSubmitting(true);

        // Start timer for self-schedule flow (BookConsult is always self-schedule)
        startTimer({
            form_type: 'coupon',
        });

        try {
            const city = siteData.selectedStore?.metro?.name;
            const state = siteData.selectedStore?.state?.abbreviation;

            const user = await handleReCaptchaVerify();
            if (user === 'bot') {
                helpers.setErrors('bot', true);
                throw new Error('Bot detected');
            } else {
                // Validate phone and email with PossibleNow
                const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                    values.phone,
                    values.email
                );

                const formData = {
                    ...values,
                    updates: true, // Always true for TCPA compliance
                    marketing_sms_optout: !CTA_CLICK_SMS_OPT_IN,
                    city,
                    state,
                    description: `Direct Mail | Book Consult | CouponCode: ${coupon.fetchedData.couponCode.toUpperCase()} | Discount: ${
                        coupon.fetchedData.discount
                    }%`,
                    cloudamp__data__c: localStorage.getItem('cloudamp__data__c') || '',
                    consultType: 'Self Schedule',
                    formSource: 'bookConsult',
                    fbp,
                    fbc,
                    experiment_exposures: appendFormUrlToExposures(experiment_exposures),
                    experiment_user_ID,
                    phoneFound,
                    wireless,
                    emailFound,
                    result,
                    flags,
                };

                // Submit to Netlify function
                const response = await axios.post(`${NETLIFY_FUNCTIONS_PATH}consult-form-handler`, formData);

                // Only sync Pardot tracking on the legacy path; leads-capture already wrote the lead.
                if (response?.status === 200 && !isLeadsCaptureResponse(response)) {
                    syncPardotTracking(values);
                }

                // FullStory Event Tracking
                FullStory('trackEvent', {
                    name: 'Coupon Consult Form - Success',
                    properties: {
                        name: 'Coupon',
                        user_email: values.email,
                        sms_optin: CTA_CLICK_SMS_OPT_IN,
                    },
                });

                // If backend returned a Self-Schedule URL, perform a top-level redirect
                if (isBrowser && response.data.redirectUrl) {
                    // Lock the overlay before the GTM await — see isRedirecting declaration above.
                    // Must be set BEFORE setSubmitting(false); otherwise React flushes the
                    // isSubmitting=false update first, causing a flash before isRedirecting=true applies.
                    setIsRedirecting(true);
                    // Push timing event to GTM before redirect
                    // Wait for event to be sent before redirecting
                    await pushTimingEvent(SELF_SCHEDULE_EVENTS.TIMING, {
                        form_type: 'coupon',
                    });

                    helpers.resetForm();
                    let redirectUrl = response.data.redirectUrl;
                    // Append GA4 cross-domain linker parameter for tracking
                    redirectUrl = appendGoogleLinkerParam(redirectUrl);
                    window.location.href = redirectUrl;
                    return;
                } else {
                    throw new Error('No redirect URL returned from backend');
                }
            }
        } catch (error) {
            console.error('Form submission error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Form submission error';

            // Track failure and reset timer on error
            await pushTimingEvent(SELF_SCHEDULE_EVENTS.FAILURE, {
                form_type: 'coupon',
                error_type: errorMessage,
            });
            await resetTimer();

            helpers.setSubmitting(false);
            helpers.setStatus('Failed');
            setIsRedirecting(false);

            // FullStory Event Tracking with enhanced error details
            FullStory('trackEvent', {
                name: 'Coupon Consult Form - Error',
                properties: {
                    name: 'Coupon',
                    user_email: values.email,
                    error_message: errorMessage,
                    error_details: axios.isAxiosError(error)
                        ? {
                              status: error.response?.status,
                              statusText: error.response?.statusText,
                          }
                        : 'Non-axios error',
                },
            });

            // Log that Pardot tracking was skipped due to form submission failure
            FullStory('trackEvent', {
                name: 'Coupon Consult Form - Pardot Sync Skipped',
                properties: {
                    user_email: values.email,
                    error_message: errorMessage,
                    reason: 'Actual Form submission failed',
                },
            });
        }
    };

    return (
        <Formik
            initialValues={couponInitialValues}
            validationSchema={couponSchema}
            onSubmit={handleSubmit}
            validateOnChange={false}
        >
            {(formik) => {
                return (
                    <Form className="w-100 card p-3 mb-5 milan-bg-white fs-mask" method="POST">
                        {formik.isSubmitting || isRedirecting ? (
                            <SelfScheduleSubmitting />
                        ) : formik.status === 'Success' ? (
                            <ConsultSuccess coupon={coupon} />
                        ) : formik.status === 'Failed' ? (
                            <FormFailed />
                        ) : (
                            <>
                                <h2 className="mb-4 subheadsm milan-text-primary">Book Your Consult</h2>
                                <div className="row mb-4 text-start">
                                    <div className="col-12 col-lg-6 mb-4 mb-lg-0">
                                        <FormikControl
                                            control="input"
                                            name="first_name"
                                            placeholder="First Name"
                                            type="text"
                                        />
                                    </div>
                                    <div className="col-12 col-lg-6">
                                        <FormikControl
                                            control="input"
                                            name="last_name"
                                            placeholder="Last Name"
                                            type="text"
                                        />
                                    </div>
                                </div>
                                <div className="mb-4 text-start">
                                    <FormikControl control="emailInput" placeholder="Your Email" name="email" />
                                </div>
                                <div className="mb-4 text-start">
                                    <FormikControl
                                        control="phoneInput"
                                        placeholder="Phone Number"
                                        name="phone"
                                        type="tel"
                                    />
                                </div>
                                <div className="mb-2 text-start">
                                    <label htmlFor="location">Location</label>
                                    <LocationsDropdownWrapper {...{ siteData, formik }} fieldName="location" />
                                </div>
                                <div className="lh-1 text-start mb-3">
                                    <MarketingConsentMsg
                                        cta={
                                            <>
                                                See Available Times <sup>*</sup>
                                            </>
                                        }
                                    />
                                </div>
                                <button
                                    className="milan-btn milan-red-btn w-100"
                                    type="submit"
                                    disabled={!formik.isValid || formik.isSubmitting}
                                >
                                    {formik.isSubmitting ? (
                                        'Submitting...'
                                    ) : (
                                        <>
                                            See Available Times <sup>*</sup>
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </Form>
                );
            }}
        </Formik>
    );
}

export default BookConsult;
